import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trophy, Calendar, Clock, CheckCircle, XCircle } from 'lucide-react';

interface UpcomingPromotion {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  description: string | null;
  age_group?: 'all' | 'kids' | 'teens' | 'adults';
}

interface BeltTestsCardProps {
  upcomingPromotions: UpcomingPromotion[];
  userRole: string | undefined;
  onConfirmPromotionAttendance: (sessionId: string, willAttend: boolean) => void;
}

const BeltTestsCard: React.FC<BeltTestsCardProps> = ({
  upcomingPromotions,
  userRole,
  onConfirmPromotionAttendance
}) => {
  useUiLanguage();
  const formatTime = (timeString: string) => {
    return new Date(`1970-01-01T${timeString}`).toLocaleTimeString(currentLocale(), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateToDDMMYYYY = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const getAgeGroupBadge = (ageGroup: string | undefined) => {
    if (!ageGroup || ageGroup === 'all') return null;

    return (
      <Badge
        variant="outline"
        className={ageGroup === 'kids' ? 'border-dojo-accent/50 text-dojo-accent bg-dojo-accent-light dark:bg-dojo-accent-light/50' : 'border-dojo-success/50 text-dojo-success bg-dojo-success-light dark:bg-dojo-success-light/50'}
      >
        {ageGroup === 'kids' ? uiText("Kids (5-12)") : ageGroup === 'teens' ? uiText("Teens (13-17)") : uiText("Adults (18+)")}
      </Badge>
    );
  };

  if (upcomingPromotions.length === 0) {
    return null;
  }

  return (
    <Card className="bg-white dark:bg-gray-800 shadow-sm">
      <CardHeader className="pb-3 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
            <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
            {uiText("Belt Progress")}</CardTitle>
          {showPromotionRequirements && (
            <Badge variant="outline" className="text-xs">
              {Math.round(currentProgress)}{uiText("% Complete")}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-4 px-4 sm:px-6">
        <div className="space-y-3 md:space-y-4">
          {upcomingPromotions.map((promotion) => (
            <div key={promotion.id} className="p-3 md:p-4 border rounded-lg bg-dojo-warning/10 border-dojo-warning/30 dark:bg-dojo-warning/5 dark:border-dojo-warning/20">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h4 className="font-semibold text-foreground text-sm md:text-base">{promotion.title}</h4>
                    <Badge variant="default">{uiText("Belt Test")}</Badge>
                    {getAgeGroupBadge(promotion.age_group)}
                  </div>

                  <div className="space-y-1 text-xs md:text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3 w-3 md:h-4 md:w-4" />
                      <span>{formatDateToDDMMYYYY(promotion.date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-3 w-3 md:h-4 md:w-4" />
                      <span>
                        {formatTime(promotion.start_time)} - {formatTime(promotion.end_time)}
                      </span>
                    </div>
                    {promotion.description && (
                      <p className="mt-2 text-muted-foreground text-xs md:text-sm">{promotion.description}</p>
                    )}
                  </div>
                </div>

                {userRole === 'student' && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onConfirmPromotionAttendance(promotion.id, false)}
                      className="border-dojo-primary/30 text-dojo-primary hover:bg-dojo-primary-light text-xs md:text-sm"
                    >
                      <XCircle className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                      {uiText("Can't Attend")}</Button>
                    <Button
                      size="sm"
                      onClick={() => onConfirmPromotionAttendance(promotion.id, true)}
                      className="bg-dojo-primary hover:bg-dojo-primary-hover text-xs md:text-sm"
                    >
                      <CheckCircle className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                      {uiText("Will Attend")}</Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default BeltTestsCard;